import IconCodeBlock from '@apollo/icons/default/IconCodeBlock.svg';
import IconDownload from '@apollo/icons/small/IconDownload.svg';
import { IconButton, Spinner } from '@apollo/orbit';
import { saveAs } from 'file-saver';
import { DocumentNode, parse } from 'graphql';
import * as monacoEditor from 'monaco-editor';
import React, { useEffect, useMemo, useState } from 'react';

import { DiffNavigatorButtons } from 'src/app/graph/shared/diffEditor/DiffNavigatorButtons';
import { HiddenSectionIndicators } from 'src/app/graph/shared/diffEditor/HiddenSectionIndicators';
import { useHideUnchangedLines } from 'src/app/graph/shared/diffEditor/useHideUnchangedLines';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import {
  StudioDiffEditorContainer,
  useModelsAndDiffEditorOutOfSync,
  useStudioDiffEditor,
} from 'src/hooks/useStudioDiffEditor';

interface Props {
  originalSchema: string;
  modifiedSchema: string;
  originalFieldCount: number;
  originalTypeCount: number;
  modifiedFieldCount: number;
  modifiedTypeCount: number;
  // The name given to the downloaded sdl file (defaults to schema.graphql)
  downloadedSdlFilename?: string;
}

export const SchemaDiffViewer = ({
  originalSchema,
  modifiedSchema,
  originalFieldCount,
  originalTypeCount,
  modifiedFieldCount,
  modifiedTypeCount,
  downloadedSdlFilename,
}: Props) => {
  const [shouldWrapLines, setShouldWrapLines] = useState<boolean>(false);
  const [shouldRenderOverviewRuler, setShouldRenderOverviewRuler] =
    useState<boolean>(true);
  const sharedMonacoOptions:
    | monacoEditor.editor.IStandaloneEditorConstructionOptions
    | monacoEditor.editor.IDiffEditorConstructionOptions = useMemo(
    () => ({
      readOnly: true,
      wordWrap: shouldWrapLines ? 'on' : 'off',
      renderSideBySide: false,
      enableSplitViewResizing: false,
      fontSize: 12,
      scrollbar: {
        useShadows: false,
        verticalScrollbarSize: 8,
        horizontalScrollbarSize: 8,
      },
      renderOverviewRuler: shouldRenderOverviewRuler,
    }),
    [shouldWrapLines, shouldRenderOverviewRuler],
  );

  const { diffEditor, diffNavigator } = useStudioDiffEditor({
    monacoOptions: sharedMonacoOptions,
    language: 'graphql',
    originalFile: originalSchema,
    modifiedFile: modifiedSchema,
  });

  diffEditor.getOriginalEditor().updateOptions({
    scrollbar: {
      vertical: 'hidden',
      horizontal: 'hidden',
      useShadows: false,
      verticalScrollbarSize: 0,
      horizontalScrollbarSize: 0,
    },
    lineNumbers: 'off',
  });

  const [typeDefs, setTypeDefs] = useState<{
    modified: DocumentNode | undefined;
    original: DocumentNode | undefined;
  }>();

  useEffect(() => {
    const disposable = diffEditor.onDidUpdateDiff(() => {
      setShouldRenderOverviewRuler(diffNavigator.canNavigate());
      const modifiedSdl = diffEditor.getModifiedEditor().getModel()?.getValue();
      const originalSdl = diffEditor.getOriginalEditor().getModel()?.getValue();
      setTypeDefs({
        modified: modifiedSdl ? parse(modifiedSdl) : undefined,
        original: originalSdl ? parse(originalSdl) : undefined,
      });
    });
    return () => disposable.dispose();
  }, [diffEditor, diffNavigator]);

  const { modifiedHiddenRanges, originalHiddenRanges, handleExpandRange } =
    useHideUnchangedLines({ diffEditor });

  const originalTypeDefs = typeDefs?.original;
  const modifiedTypeDefs = typeDefs?.modified;
  const shouldShowCollapsedSections = Boolean(
    originalTypeDefs &&
      modifiedTypeDefs &&
      originalHiddenRanges &&
      modifiedHiddenRanges,
  );

  const modelsAndDiffOutOfSync = useModelsAndDiffEditorOutOfSync({
    diffEditor,
  });

  return (
    <div>
      {!modelsAndDiffOutOfSync && shouldShowCollapsedSections && (
        <>
          <HiddenSectionIndicators
            editor={diffEditor.getModifiedEditor()}
            typeDefs={modifiedTypeDefs as DocumentNode}
            rangesToHide={modifiedHiddenRanges as monacoEditor.Range[]}
            handleExpandRange={handleExpandRange}
          />
          <HiddenSectionIndicators
            editor={diffEditor.getOriginalEditor()}
            typeDefs={originalTypeDefs as DocumentNode}
            rangesToHide={originalHiddenRanges as monacoEditor.Range[]}
            handleExpandRange={handleExpandRange}
          />
        </>
      )}

      <div className="flex w-full items-center border border-b-0 border-silver-dark py-1.5 shadow-sm">
        <div className="ml-2 flex flex-1">
          <TypesAndFieldsCount
            fieldCount={modifiedFieldCount}
            fieldCountDelta={modifiedFieldCount - originalFieldCount}
            typeCount={modifiedTypeCount}
            typeCountDelta={modifiedTypeCount - originalTypeCount}
          />
        </div>
        <div className="flex justify-end">
          {!diffNavigator.canNavigate() && (
            <div className="my-auto mr-2.5 text-sm text-secondary">
              0 changes
            </div>
          )}
          <DiffNavigatorButtons
            diffEditor={diffEditor}
            diffNavigator={diffNavigator}
          />
          <div className="mr-2 space-x-1">
            <WrapLinesButton
              wrap={shouldWrapLines}
              onClick={() => setShouldWrapLines(!shouldWrapLines)}
            />
            <DownloadSdlButton
              onClick={() => {
                saveAs(
                  new Blob([modifiedSchema], {
                    type: 'text/plain;charset=utf-8',
                  }),
                  downloadedSdlFilename
                    ? `${downloadedSdlFilename}.graphql`
                    : 'schema.graphql',
                );
              }}
            />
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-b border border-silver-dark shadow-sm">
        {modelsAndDiffOutOfSync ? (
          <div className="my-2.5 flex h-80 w-full items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <StudioDiffEditorContainer
            diffEditor={diffEditor}
            editorHeight="340px"
          />
        )}
      </div>
    </div>
  );
};

function TypesAndFieldsCount({
  typeCount,
  fieldCount,
  typeCountDelta,
  fieldCountDelta,
}: {
  typeCount: number;
  fieldCount: number;
  typeCountDelta: number;
  fieldCountDelta: number;
}) {
  return (
    <div className="flex items-center text-sm text-secondary">
      <div className="mr-1.5">
        <span className="font-semibold text-neutral">{typeCount}</span> types{' '}
        {typeCountDelta !== 0 && (
          <span>
            (
            {typeCountDelta > 0 && (
              <span className="font-semibold text-green">
                +{typeCountDelta}
              </span>
            )}
            {typeCountDelta < 0 && (
              <span className="font-semibold text-red">{typeCountDelta}</span>
            )}
            )
          </span>
        )}
      </div>
      <div>
        <span className="font-semibold text-neutral">{fieldCount}</span> fields{' '}
        {fieldCountDelta !== 0 && (
          <span>
            (
            {fieldCountDelta > 0 && (
              <span className="font-semibold text-green">
                +{fieldCountDelta}
              </span>
            )}
            {fieldCountDelta < 0 && (
              <span className="font-semibold text-red">{fieldCountDelta}</span>
            )}
            )
          </span>
        )}
      </div>
    </div>
  );
}

function DownloadSdlButton({ onClick }: { onClick: () => void }) {
  return (
    <Tooltip label="Download preview schema" placement="top">
      <IconButton
        aria-label="Download preview schema"
        variant="hidden"
        size="sm"
        onClick={onClick}
      >
        <IconDownload />
      </IconButton>
    </Tooltip>
  );
}

export function WrapLinesButton({
  wrap,
  onClick,
}: {
  wrap: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip label={wrap ? 'No line wrap' : 'Line wrap'} placement="top">
      <IconButton
        aria-label={wrap ? 'No line wrap' : 'Line wrap'}
        variant="hidden"
        size="sm"
        onClick={onClick}
        icon={<IconCodeBlock />}
      />
    </Tooltip>
  );
}
